/**
 * Exportar la lista de productos para pasarsela a un proveedor.
 *
 * EL CASO REAL: el dueño termino de relevar los perfumes y un proveedor le
 * pidio la lista para ver si puede mejorar los precios. La lista que sirve
 * para eso lleva el COSTO actual (el numero que el proveedor tiene que
 * ganar), no el precio de venta ni el margen: eso es del negocio y no viaja.
 *
 * Tres formatos porque hay tres proveedores distintos:
 *  - Excel: el que cotiza en serio agrega su columna y devuelve la planilla.
 *    Lleva el codigo interno, asi la planilla que vuelve es re-importable por
 *    el flujo de listas sin adivinar que producto es cada fila.
 *  - PDF: el que solo la va a leer.
 *  - Texto: el que la quiere en el chat, directo.
 *
 * Las librerias (xlsx, jspdf) se cargan con `import()` recien al exportar:
 * son pesadas y esto se usa una vez cada tanto. Nada de aca toca React.
 */
import { format } from "date-fns";

import { formatear, formatearNumero, type Moneda } from "@tora/shared";

import { aNumero, type Producto } from "@/lib/tipos";

export interface FilaExportar {
  codigo: string;
  nombre: string;
  /** `null` = sin costo cargado. NO es cero: cero seria mercaderia regalada. */
  costo: number | null;
  moneda: Moneda;
}

/**
 * Las filas que van al archivo, en el orden en que estan en la pantalla.
 *
 * Un costo en cero es "todavia no se cargo" (asi entraron 273 productos de las
 * planillas, y `relevar_producto` exige costo > 0): en la lista va vacio, para
 * que el proveedor no cotice contra un cero que no existe.
 */
export function filasParaExportar(productos: ReadonlyArray<Producto>): FilaExportar[] {
  return productos.map((p) => {
    const costo = aNumero(p.costo);
    return {
      codigo: p.codigo_interno,
      nombre: p.nombre,
      costo: costo > 0 ? costo : null,
      moneda: p.costo_moneda,
    };
  });
}

/** "Perfumes" → "lista-perfumes-2026-09-02.xlsx". Sin acentos ni espacios. */
export function nombreArchivo(seccion: string, extension: string, fecha = new Date()): string {
  const slug = seccion
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `lista-${slug}-${format(fecha, "yyyy-MM-dd")}.${extension}`;
}

/**
 * La lista como texto de WhatsApp: se copia y se pega en el chat.
 *
 * El costo va formateado con su simbolo ("US$ 18,00" / "$ 15.000,00") porque
 * en un chat no hay encabezado de columna que diga la moneda, y en este
 * negocio conviven las dos.
 */
export function textoLista(params: {
  titulo: string;
  negocio: string;
  filas: ReadonlyArray<FilaExportar>;
  fecha?: Date;
}): string {
  const { titulo, negocio, filas, fecha = new Date() } = params;

  const lineas = filas.map((f) =>
    f.costo === null ? `• ${f.nombre}` : `• ${f.nombre} — ${formatear(f.costo, f.moneda)}`,
  );

  return [
    `*${titulo} — ${negocio}*`,
    `Lista para cotizar · ${format(fecha, "dd/MM/yyyy")}`,
    "",
    ...lineas,
    "",
    `${filas.length} producto${filas.length === 1 ? "" : "s"}`,
  ].join("\n");
}

const COLUMNAS = ["Código", "Producto", "Costo", "Moneda"] as const;

/**
 * Descarga la planilla .xlsx. El costo va como NUMERO crudo (sin formato):
 * el proveedor va a hacer cuentas sobre esa columna, no a leerla.
 */
export async function descargarExcel(params: {
  seccion: string;
  filas: ReadonlyArray<FilaExportar>;
}): Promise<void> {
  const XLSX = await import("xlsx");
  const { seccion, filas } = params;

  const hoja = XLSX.utils.aoa_to_sheet([
    [...COLUMNAS],
    ...filas.map((f) => [f.codigo, f.nombre, f.costo ?? "", f.costo === null ? "" : f.moneda]),
  ]);
  hoja["!cols"] = [{ wch: 10 }, { wch: 46 }, { wch: 12 }, { wch: 8 }];

  const libro = XLSX.utils.book_new();
  // El nombre de hoja tiene tope de 31 caracteres en el formato.
  XLSX.utils.book_append_sheet(libro, hoja, seccion.slice(0, 31));
  XLSX.writeFile(libro, nombreArchivo(seccion, "xlsx"));
}

/** Descarga el PDF: la misma tabla, con el negocio y la fecha en el encabezado. */
export async function descargarPdf(params: {
  seccion: string;
  negocio: string;
  filas: ReadonlyArray<FilaExportar>;
}): Promise<void> {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const { seccion, negocio, filas } = params;

  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text(`${seccion} — ${negocio}`, 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(
    `Lista para cotizar · ${format(new Date(), "dd/MM/yyyy")} · ${filas.length} productos`,
    14,
    25,
  );

  autoTable(doc, {
    startY: 31,
    head: [[...COLUMNAS]],
    body: filas.map((f) => [
      f.codigo,
      f.nombre,
      f.costo === null ? "" : formatearNumero(f.costo),
      f.costo === null ? "" : f.moneda,
    ]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [38, 38, 38] },
    columnStyles: { 2: { halign: "right" } },
  });

  doc.save(nombreArchivo(seccion, "pdf"));
}
